/**
 * FloatingActionBar — draggable vertical button group on the right side.
 * Marketplace page: Favorites filter/list + Compare + Chat.
 * Listing detail page: Favorite + Buy + Chat.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Dropdown,
  Form,
  Input,
  List,
  Modal,
  Popover,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BarChartOutlined,
  CrownOutlined,
  DashboardOutlined,
  GiftOutlined,
  HeartFilled as _HeartFilled,
  HeartOutlined as _HeartOutlined,
  LogoutOutlined,
  MessageOutlined,
  ShopOutlined,
  ShoppingOutlined,
  StarFilled,
  StarOutlined,
  UserOutlined,
  WalletOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { checkGet } from '../../api/checkClient';
import { startDeal } from '../../api/deals';
import {
  expressMarketplaceInterest,
  getMarketplaceInterest,
  withdrawMarketplaceInterest,
  type MarketplaceInterestResponse,
} from '../../api/marketplaceInterests';
import { useAuthStore } from '../../stores/authStore';
import { useChatCheckStore } from '../../stores/chatCheckStore';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { useMarketplaceActionStore } from '../../stores/marketplaceActionStore';
import { getDisplayRole } from '../../utils/productRole';
import { getUserCapabilities } from '../../utils/accountCapabilities';

const { Text } = Typography;

const BUTTON_SIZE = 44;
const BAR_WIDTH = 52;
const DEFAULT_RIGHT = 16;
const DEFAULT_TOP = 120;

const roleColor: Record<string, string> = {
  person: '#1668dc',
  enterprise: '#1668dc',
  buyer: '#d48806',
  investor: '#d48806',
  admin: '#f5222d',
  operator: '#389e0d',
  dev: '#722ed1',
};

const FloatingActionBar: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const isOpen = useChatCheckStore((s) => s.isOpen);
  const setOpen = useChatCheckStore((s) => s.setOpen);
  const messages = useChatCheckStore((s) => s.messages);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(user?.credits_remaining ?? null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [dealLoading, setDealLoading] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [buyForm] = Form.useForm<{ note: string }>();
  const [pageInterest, setPageInterest] = useState<MarketplaceInterestResponse | null>(null);
  const [favListOpen, setFavListOpen] = useState(false);
  const [myPurchasesOpen, setMyPurchasesOpen] = useState(false);

  const favoriteIds = useFavoritesStore((s) => s.ids);
  const favoriteNames = useFavoritesStore((s) => s.names);
  const toggleFavorite = useFavoritesStore((s) => s.toggle);

  const showFavoritesOnly = useMarketplaceActionStore((s) => s.showFavoritesOnly);
  const setShowFavoritesOnly = useMarketplaceActionStore((s) => s.setShowFavoritesOnly);
  const selectedCompareIds = useMarketplaceActionStore((s) => s.selectedCompareIds);
  const clearCompareIds = useMarketplaceActionStore((s) => s.clearCompareIds);
  const pageListingId = useMarketplaceActionStore((s) => s.pageListingId);
  const pageListingStatus = useMarketplaceActionStore((s) => s.pageListingStatus);
  const submittedInterestIds = useMarketplaceActionStore((s) => s.submittedInterestIds);
  const submittedInterestNames = useMarketplaceActionStore((s) => s.submittedInterestNames);
  const addSubmittedInterest = useMarketplaceActionStore((s) => s.addSubmittedInterest);
  const removeSubmittedInterest = useMarketplaceActionStore((s) => s.removeSubmittedInterest);

  const [pos, setPos] = useState({ top: DEFAULT_TOP, right: DEFAULT_RIGHT });
  const dragRef = useRef<{ startX: number; startY: number; origTop: number; origRight: number } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const caps = getUserCapabilities(user);
  const canFavorite = !!user && ['person', 'enterprise', 'buyer', 'investor'].includes(user?.role ?? '');
  const compareCount = selectedCompareIds.size;
  const isFavoritedListing = !!pageListingId && favoriteIds.has(pageListingId);
  const isListingSold = pageListingStatus === 'sold';
  const buyAlreadySubmitted = !!pageListingId && submittedInterestIds.has(pageListingId);
  const isListingLocked = ['under_offer', 'in_due_diligence', 'in_escrow', 'closing'].includes(pageListingStatus ?? '');

  // prevListingId ref kept for potential future use
  const prevListingId = useRef<string | null>(null);
  useEffect(() => {
    prevListingId.current = pageListingId;
  }, [pageListingId]);

  const loadCredits = useCallback(async () => {
    try {
      const balance = await checkGet<{ credits_remaining: number }>('/credits/balance');
      setCreditsRemaining(balance.credits_remaining);
    } catch {
      setCreditsRemaining(user?.credits_remaining ?? null);
    }
  }, [user?.credits_remaining]);

  useEffect(() => {
    setCreditsRemaining(user?.credits_remaining ?? null);
  }, [user?.credits_remaining]);

  useEffect(() => {
    void loadCredits();
    const onFocus = () => { void loadCredits(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadCredits]);

  useEffect(() => {
    if (!pageListingId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMarketplaceInterest(pageListingId);
        if (cancelled) return;
        setPageInterest(res.status === 'none' ? null : res);
        if (res.status === 'open' || res.status === 'in_progress' || res.status === 'completed') {
          addSubmittedInterest(pageListingId, res.plant_name);
        } else if (res.status === 'none' || res.status === 'cancelled' || res.status === 'rejected') {
          removeSubmittedInterest(pageListingId);
        }
      } catch {
        // The listing action state is advisory; keep the current UI state on transient failures.
      }
    })();
    return () => { cancelled = true; };
  }, [pageListingId, user, addSubmittedInterest, removeSubmittedInterest]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origTop: pos.top, origRight: pos.right };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      const barH = barRef.current?.offsetHeight ?? 220;
      const newTop = Math.max(8, Math.min(window.innerHeight - barH - 8, dragRef.current.origTop + dy));
      const newRight = Math.max(8, Math.min(window.innerWidth - BAR_WIDTH - 8, dragRef.current.origRight - dx));
      setPos({ top: newTop, right: newRight });
    };

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const unreadDot = useMemo(() => messages.some((m) => m.role === 'assistant'), [messages]);

  const withdrawCurrentInterest = useCallback(async () => {
    if (!pageListingId) return;
    setBuyLoading(true);
    try {
      const current = pageInterest?.support_request_id ? pageInterest : await getMarketplaceInterest(pageListingId);
      if (current.status === 'none' || current.status === 'cancelled' || current.status === 'rejected') {
        removeSubmittedInterest(pageListingId);
        setPageInterest(null);
        message.info('Purchase interest is already inactive.');
        return;
      }
      if (current.status !== 'open' && current.status !== 'in_progress') {
        setPageInterest(current);
        message.info('This interest is no longer pending and cannot be withdrawn.');
        return;
      }
      if (!current.support_request_id) {
        message.error('Cannot find the pending interest request.');
        return;
      }
      await withdrawMarketplaceInterest(current.support_request_id);
      removeSubmittedInterest(pageListingId);
      setPageInterest(null);
      message.success('Purchase interest withdrawn');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Failed to withdraw interest');
    } finally {
      setBuyLoading(false);
    }
  }, [pageInterest, pageListingId, removeSubmittedInterest]);

  if (!user) return null;

  const role = user.role || 'person';
  const displayName = user.email?.split('@')[0] || 'User';
  const displayRole = user.display_role || getDisplayRole(role);

  let menuItems: MenuProps['items'];
  if (caps.use_external_menu) {
    menuItems = [
      { key: 'h', type: 'group', label: <div><Text strong style={{ color: '#fff' }}>{displayName}</Text><br /><Tag color={roleColor[role] ?? 'default'} style={{ marginTop: 2 }}>{displayRole}</Tag></div> },
      { type: 'divider' },
      { key: 'projects', icon: <DashboardOutlined />, label: 'My Projects', onClick: () => navigate('/portfolio') },
      ...(caps.can_browse_marketplace ? [{ key: 'marketplace', icon: <ShopOutlined />, label: 'Market', onClick: () => navigate('/marketplace') }] : []),
      { key: 'billing', icon: <WalletOutlined />, label: 'Billing & Plan', onClick: () => navigate('/billing') },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: () => { logout(); navigate('/login'); } },
    ];
  } else if (role === 'operator') {
    menuItems = [
      { key: 'h', type: 'group', label: <div><Text strong style={{ color: '#fff' }}>{displayName}</Text><br /><Tag color={roleColor[role]} style={{ marginTop: 2 }}>{displayRole}</Tag></div> },
      { type: 'divider' },
      { key: 'approvals', icon: <CrownOutlined />, label: 'My Approvals', onClick: () => navigate('/operator/approvals') },
      { key: 'projects', icon: <DashboardOutlined />, label: 'Project Portfolio', onClick: () => navigate('/portfolio') },
      { key: 'sales', icon: <GiftOutlined />, label: 'My Sales', onClick: () => navigate('/operator/sales') },
      { key: 'marketplace', icon: <ShopOutlined />, label: 'Market', onClick: () => navigate('/marketplace') },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: () => { logout(); navigate('/login'); } },
    ];
  } else {
    menuItems = [
      { key: 'h', type: 'group', label: <div><Text strong style={{ color: '#fff' }}>{displayName}</Text><br /><Tag color={roleColor[role] ?? 'default'} style={{ marginTop: 2 }}>{displayRole}</Tag></div> },
      { type: 'divider' },
      { key: 'approval', icon: <CrownOutlined />, label: 'Approval Dashboard', onClick: () => navigate('/admin') },
      { key: 'plants', icon: <DashboardOutlined />, label: 'Plant Analytics', onClick: () => navigate('/admin/plants') },
      ...(role === 'admin' || role === 'dev' ? [{ key: 'users', icon: <CrownOutlined />, label: 'User Admin', onClick: () => navigate('/admin/users') }] : []),
      { type: 'divider' },
      { key: 'billing', icon: <WalletOutlined />, label: 'Billing & Plan', onClick: () => navigate('/admin/billing') },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: () => { logout(); navigate('/login'); } },
    ];
  }

  const btnStyle: React.CSSProperties = {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: '#111821',
    border: '1px solid rgba(255,255,255,0.14)',
    transition: 'background 0.15s',
    flexShrink: 0,
  };

  const favListContent = (
    <div style={{ width: 260, maxHeight: 320, overflowY: 'auto' }}>
      {favoriteIds.size === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>No favorites yet</Text>
      ) : (
        <List
          size="small"
          dataSource={[...favoriteIds]}
          renderItem={(id: string) => (
            <List.Item
              style={{ cursor: 'pointer', padding: '6px 0' }}
              onClick={() => { navigate(`/marketplace/listings/${id}?from=/marketplace`); setFavListOpen(false); }}
            >
              <Text style={{ fontSize: 12 }}>{favoriteNames[id] || id.slice(0, 8)}</Text>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <div
      ref={barRef}
      style={{
        position: 'fixed',
        top: pos.top,
        right: pos.right,
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        userSelect: 'none',
      }}
    >
      <div
        onMouseDown={onMouseDown}
        style={{ width: BAR_WIDTH, height: 20, cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ width: 24, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.25)' }} />
      </div>

      <Dropdown
        menu={{ items: menuItems, theme: 'dark', style: { minWidth: 220, background: '#111821' } }}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ zIndex: 3001 }}
      >
        <Tooltip title={displayName} placement="left">
          <div style={btnStyle}>
            <Avatar size={28} icon={<UserOutlined />} style={{ backgroundColor: roleColor[role] ?? '#1668dc' }} />
          </div>
        </Tooltip>
      </Dropdown>

      {creditsRemaining != null && (
        <Tooltip title="Credits remaining" placement="left">
          <div
            style={{ ...btnStyle, cursor: 'pointer', flexDirection: 'column', gap: 0, padding: '4px 2px' }}
            onClick={() => navigate('/billing')}
          >
            <Text style={{ color: '#8cb4ff', fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>
              {creditsRemaining >= 1000 ? `${(creditsRemaining / 1000).toFixed(1)}k` : creditsRemaining}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, lineHeight: 1.2 }}>cr</Text>
          </div>
        </Tooltip>
      )}

      {!!pageListingId && (
        <>
          {/* Interest — independent from deal workflow. */}
          <Tooltip
            title={
              isListingSold
                ? 'This asset has been sold'
                : buyAlreadySubmitted
                  ? 'Interest submitted. Click to withdraw while pending.'
                  : isListingLocked
                    ? 'This listing is already in a deal workflow'
                : 'Request owner contact'
            }
            placement="left"
          >
            <div
              style={{
                ...btnStyle,
                background: isListingSold
                  ? '#111821'
                  : buyAlreadySubmitted
                    ? 'rgba(22,104,220,0.85)'
                    : '#111821',
                border: isListingSold
                  ? '1px solid rgba(255,255,255,0.14)'
                  : buyAlreadySubmitted
                    ? '1px solid rgba(22,104,220,0.6)'
                    : '1px solid rgba(255,255,255,0.14)',
                cursor: isListingSold || (isListingLocked && !buyAlreadySubmitted) ? 'not-allowed' : 'pointer',
                opacity: isListingSold ? 0.4 : 1,
              }}
              onClick={isListingSold || (isListingLocked && !buyAlreadySubmitted)
                ? undefined
                : buyAlreadySubmitted
                  ? () => {
                      Modal.confirm({
                        title: 'Withdraw contact request?',
                        content: 'This withdraws your request to contact the project owner.',
                        okText: 'Withdraw',
                        okButtonProps: { danger: true },
                        cancelText: 'Cancel',
                        onOk: withdrawCurrentInterest,
                      });
                    }
                  : () => { buyForm.resetFields(); setBuyModalOpen(true); }}
            >
              <RiseOutlined style={{ color: buyAlreadySubmitted ? '#fff' : 'rgba(255,255,255,0.5)', fontSize: 18 }} spin={buyLoading && buyAlreadySubmitted} />
            </div>
          </Tooltip>

          {/* Purchase workflow — independent from interest state. */}
          <Tooltip
            title={
              isListingSold
                ? 'This asset has been sold'
                : isListingLocked
                  ? 'This listing is already in a deal workflow'
                  : 'Start purchase workflow'
            }
            placement="left"
          >
            <div
              style={{
                ...btnStyle,
                background: '#111821',
                border: '1px solid rgba(255,255,255,0.14)',
                cursor: isListingSold || isListingLocked ? 'not-allowed' : 'pointer',
                opacity: isListingSold || isListingLocked ? 0.45 : 1,
              }}
              onClick={isListingSold || isListingLocked
                ? undefined
                : async () => {
                    if (!pageListingId) return;
                    setDealLoading(true);
                    try {
                      const deal = await startDeal(pageListingId);
                      navigate(`/deals/${deal.id}`);
                    } catch (e) {
                      message.error(e instanceof Error ? e.message : 'Failed to start purchase workflow');
                    } finally {
                      setDealLoading(false);
                    }
                  }}
            >
              <ShoppingOutlined style={{ color: 'rgba(255,255,255,0.72)', fontSize: 18 }} spin={dealLoading} />
            </div>
          </Tooltip>

          <Modal
            open={buyModalOpen}
            title="Request Owner Contact"
            okText="Submit"
            cancelText="Cancel"
            confirmLoading={buyLoading}
            onCancel={() => { setBuyModalOpen(false); buyForm.resetFields(); }}
            onOk={async () => {
              if (!pageListingId) return;
              setBuyLoading(true);
              try {
                const res = await expressMarketplaceInterest(pageListingId);
                setPageInterest(res);
                const plantName = favoriteNames[pageListingId] || submittedInterestNames[pageListingId];
                addSubmittedInterest(pageListingId, plantName);
                // Auto-favorite after submitting interest
                if (!isFavoritedListing) void toggleFavorite(pageListingId);
                setBuyModalOpen(false);
                buyForm.resetFields();
                message.success('Contact request submitted. QE will review and follow up.');
              } catch (e) {
                message.error(e instanceof Error ? e.message : 'Failed to submit');
              } finally {
                setBuyLoading(false);
              }
            }}
          >
            <Form form={buyForm} layout="vertical" style={{ marginTop: 8 }}>
              <p style={{ color: 'rgba(255,255,255,0.72)', marginBottom: 16 }}>
                You are asking QE to review your fund contact request for this authorized project. Owner contact is released only after approval.
              </p>
              <Form.Item name="note" label="Message to operator (optional)">
                <Input.TextArea rows={3} placeholder="e.g. Please review our fund contact request and LOI readiness." />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}

      {!pageListingId && (
        <>
          {canFavorite && (
            <Popover
              open={favListOpen}
              onOpenChange={setFavListOpen}
              content={favListContent}
              title={<Text strong style={{ color: '#fff' }}>My Favorites ({favoriteIds.size})</Text>}
              placement="left"
              trigger="click"
              styles={{ root: { zIndex: 3001 }, body: { background: '#111821', border: '1px solid rgba(255,255,255,0.14)', padding: '12px 16px' } }}
            >
              <Tooltip title={showFavoritesOnly ? 'Show all listings' : `My Favorites (${favoriteIds.size})`} placement="left">
                <div
                  style={{
                    ...btnStyle,
                    background: showFavoritesOnly ? 'rgba(250,173,20,0.18)' : '#111821',
                    border: showFavoritesOnly ? '1px solid rgba(250,173,20,0.6)' : '1px solid rgba(255,255,255,0.14)',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFavoritesOnly(!showFavoritesOnly);
                  }}
                  onContextMenu={(e) => { e.preventDefault(); setFavListOpen(true); }}
                >
                  {showFavoritesOnly
                    ? <StarFilled style={{ color: '#faad14', fontSize: 18 }} />
                    : <Badge count={favoriteIds.size} size="small" offset={[6, -6]}>
                        <StarOutlined style={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
                      </Badge>}
                </div>
              </Tooltip>
            </Popover>
          )}

          {compareCount >= 2 && (
            <Tooltip title={`Compare ${compareCount} assets`} placement="left">
              <div
                style={{
                  ...btnStyle,
                  background: 'rgba(82,196,26,0.18)',
                  border: '1px solid rgba(82,196,26,0.5)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  navigate(`/compare?ids=${[...selectedCompareIds].join(',')}`);
                  clearCompareIds();
                }}
              >
                <Badge count={compareCount} size="small" offset={[6, -6]}>
                  <BarChartOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                </Badge>
              </div>
            </Tooltip>
          )}

          {submittedInterestIds.size > 0 && (
            <Popover
              open={myPurchasesOpen}
              onOpenChange={setMyPurchasesOpen}
              placement="left"
              trigger="click"
              title={<Text strong style={{ color: '#fff' }}>My Contact Requests ({submittedInterestIds.size})</Text>}
              styles={{ root: { zIndex: 3001 }, body: { background: '#111821', border: '1px solid rgba(255,255,255,0.14)', padding: '12px 16px' } }}
              content={
                <div style={{ width: 260, maxHeight: 320, overflowY: 'auto' }}>
                  <List
                    size="small"
                    dataSource={[...submittedInterestIds]}
                    renderItem={(id: string) => (
                      <List.Item
                        style={{ cursor: 'pointer', padding: '6px 0' }}
                        onClick={() => { navigate(`/rating/${id}`, { state: { from: '/marketplace' } }); setMyPurchasesOpen(false); }}
                      >
                        <Text style={{ fontSize: 12 }}>{submittedInterestNames[id] || id.slice(0, 8)}</Text>
                      </List.Item>
                    )}
                  />
                </div>
              }
            >
              <Tooltip title={`My Contact Requests (${submittedInterestIds.size})`} placement="left">
                <div
                  style={{
                    ...btnStyle,
                    background: 'rgba(22,104,220,0.15)',
                    border: '1px solid rgba(22,104,220,0.5)',
                    cursor: 'pointer',
                  }}
                >
                  <Badge count={submittedInterestIds.size} size="small" offset={[6, -6]}>
                    <ShoppingOutlined style={{ color: '#4096ff', fontSize: 18 }} />
                  </Badge>
                </div>
              </Tooltip>
            </Popover>
          )}
        </>
      )}

      <Tooltip title="AI Assistant" placement="left">
        <div
          style={{
            ...btnStyle,
            background: isOpen ? 'rgba(22,104,220,0.85)' : '#111821',
          }}
          onClick={() => setOpen(!isOpen)}
        >
          <Badge dot={unreadDot && !isOpen} offset={[4, -4]}>
            <MessageOutlined style={{ color: '#fff', fontSize: 18 }} />
          </Badge>
        </div>
      </Tooltip>
    </div>
  );
};

export default FloatingActionBar;
